import {observer} from "mobx-react-lite";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {reaction, runInAction} from "mobx";
import {AdminStore} from "./stores/admin_store";
import {MdChevronLeft, MdChevronRight, MdClose, MdFirstPage, MdHandshake, MdLastPage} from "react-icons/md"
import {AdminStoreContext} from "./protected_route";
import styled from "styled-components";
import {AdminButton, AdminScaffold} from "./admin_scaffold";
import {CvvRoutes, CvvStrings} from "../strings";
import {AdminHeader} from "./components/admin_decorations";
import {useNavigate} from "react-router-dom";
import {DateFormatters} from "../utils";
import {CvvColors, StyledProps} from "../theme";
import {admin_participants, billing, cvv_types} from "../proto/compiled";
import {useUrlState, participantsUrlSchema} from "./stores/admin_url_state";
import {AdminParticipant} from "./admin_participant";
import {MdDriveEta} from "react-icons/md";
import {IconButton} from "../forms/components/decorations";

const ParticipantsGrid = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: auto 180px 200px 130px 210px 130px;
    gap: 1px;
    margin-bottom: 10px;
`;

const ParticipantsGridHeaderCell = styled.div`
    text-align: center;
    line-height: 27px;
    height: 27px;
    outline: solid 1px #CCCCCC;
    background-color: ${({theme}) => theme.content10};
    overflow: hidden;
    ${({theme}: StyledProps) => theme.textTheme.body.with({color: 'black', weight: 'bold'}).css};
`;

const SortableHeaderCell = styled(ParticipantsGridHeaderCell)<{active?: boolean}>`
    cursor: pointer;
    user-select: none;
    &:hover {
        background-color: ${({theme}) => theme.content20};
    }
    ${({active}) => active && `text-decoration: underline;`}
`;

const ParticipantsGridCell = styled.div`
    display: flex;
    outline: solid 1px #CCCCCC;
    line-height: 15px;
    padding: 2px 5px;
    overflow: hidden;
    align-items: center;
    ${({theme}: StyledProps) => theme.textTheme.body.with({color: 'black'}).css};
`;

const Select = styled.select`
`;

const Container = styled.div`
    flex-direction: column;
    display: flex;
    align-items: center;
`;

const RowContainer = styled.div<{ selected: boolean, archived: boolean }>`
    min-height: 50px;
    & > * {
        background-color: ${({theme, archived, selected}: StyledProps) => selected ? theme.content10 : archived ? 'rgba(255,143,143,0.47)' : undefined};
        font-weight: ${({selected}: StyledProps) => selected ? 'bold' : 'normal'};
        color: ${({theme, archived}: StyledProps) => archived ? theme.content50 : undefined};
        min-height: 30px;
    }

    & > :nth-child(1) {
        transition: border 300ms;
        ${({
               theme,
               selected
           }: StyledProps) => selected ? `border-left: solid 10px ${theme.primary}` : `border-left: solid 0 ${theme.primary}`};
    }
`;

const CreditCard = () => {
  return <div style={{ width: '18px', height: '20px', position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)'}}>
  <svg width="18px" height="20px" viewBox="0 0 72 50" id="emoji" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="72" height="50" fill="black" rx="4" ry="4"/>
    <rect x="0" y="10" width="72" height="12" fill="#ffffff" />
  </svg>
  </div>;
}

export const AdminParticipants = observer(() => {
  const store = useContext(AdminStoreContext) as AdminStore;
  const navigate = useNavigate();
  const {values, setValues} = useUrlState(participantsUrlSchema);
  const prevRef = useRef<typeof values | null>(null);
  const didHydrate = useRef(false);

  // (A) Filters: URL -> store fields -> list load. Runs on mount AND on every param
  //     change (back/forward, shared links). `osebe` is handled separately (C)/(D).
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = values;

    runInAction(() => {
      store.tecajTip = values.tip;
      store.tecaj = values.tecaj;
      store.dodatniPogoji = values.pogoji;
      store.dodatniPlacilniPogoji = values.placilo;
      store.participants.search = values.search;
      store.participants.sortBy = values.sort;
      store.participants.page = values.page;
      store.participants.date = values.date;
    });

    // A fresh Date object is decoded every render, so compare dates by value —
    // otherwise every keystroke looks like a filter change and defeats the debounce.
    const sameDate = (a?: Date, b?: Date) => (a?.getTime() ?? 0) === (b?.getTime() ?? 0);
    const listChanged = !prev
      || values.tip !== prev.tip
      || values.tecaj !== prev.tecaj
      || values.pogoji !== prev.pogoji
      || values.placilo !== prev.placilo
      || values.sort !== prev.sort
      || values.page !== prev.page
      || !sameDate(values.date, prev.date);
    const searchChanged = !prev || values.search !== prev.search;

    if (listChanged) store.participants.loadParticipants();          // structural -> immediate
    else if (searchChanged) store.participants.loadParticipantsDebounced(); // typing -> debounced
    // else: only `osebe` changed -> no list reload
  }, [values, store]);

  // (B) One-time on mount: page-wide reference data (comment types).
  useEffect(() => {
    if (didHydrate.current) return;
    didHydrate.current = true;
    store.participants.loadCommentTypes();
  }, [store]);

  // (C) URL -> open person. Runs on mount (deep-link) and on back/forward.
  //     `undefined` closes the panel. Guarded so it's a no-op when already in sync.
  useEffect(() => {
    const id = values.osebe;
    if (id === (store.participants.participant?.summary?.sessionId ?? undefined)) return;
    store.participants.setParticipant(id);
  }, [values.osebe, store]);

  // (D) Open person -> URL. setParticipant is still called directly from the row/detail
  //     UI (many call sites), so this mirrors the resulting selection into the URL for
  //     deep-linking. Guarded so hydrating from a deep link doesn't push a dead history entry.
  useEffect(() => reaction(
    () => store.participants.participant?.summary?.sessionId ?? undefined,
    (id) => {
      const cur = new URLSearchParams(window.location.search).get('osebe') ?? undefined;
      if (id === cur) return;
      setValues({osebe: id}, {replace: false});
    },
  ), [store, setValues]);

  const getRow = useCallback((participant: admin_participants.IParticipantSummary) => {
    const start = participant.tecajStart ? new Date(participant.tecajStart!.ts as number) : undefined;
    let color = 'transparent' as string | undefined;
    if (participant.archived) {
      color = undefined;
    } else {
      switch (participant.paymentStatus) {
        case billing.PaymentStatus.no_proforma_invoice:
          color = '#fff4b0';
          break;
        case billing.PaymentStatus.proforma_invoice:
          if (participant.paymentType === billing.PaymentType.gotovina) {
            color = '#d4efff'
          } else {
            color = '#acb1d7';
          }
          break;
        case billing.PaymentStatus.amount_mismatch:
          color = '#ffcccb';
          break;
        case billing.PaymentStatus.paid:
        case billing.PaymentStatus.invoice_created:
          color = '#c9ffc0';
          break;
      }
    }
    let paymentStatus = CvvStrings.placilo[participant.paymentStatus ?? billing.PaymentStatus.no_proforma_invoice];
    if ((participant.paymentStatus === billing.PaymentStatus.paid && participant.navInvoice?.length) || participant.paymentStatus === billing.PaymentStatus.invoice_created) { // we keep the first part for backwards compatibility
      paymentStatus = 'RAČUN IZDAN';
    }

    return <RowContainer
      key={participant.sessionId!}
      archived={!!participant.archived}
      selected={participant.sessionId === store.participants.participant?.summary?.sessionId}
      style={{
        display: 'contents',
        cursor: 'pointer',
      }}
      onClick={() => store.participants.setParticipant(participant.sessionId!)}>
      <ParticipantsGridCell>
        {participant.tecajTipName?.toUpperCase()} - {participant.tecajName?.toUpperCase()}
      </ParticipantsGridCell>
      <ParticipantsGridCell style={{
        backgroundColor: (participant.archived || start) ? undefined : CvvColors.warnYellow,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center'
      }}>
        {start &&
            ((participant.tecajDates?.length ?? 0) > 1 ?
                participant.tecajDates?.map((d) => <span>{participant.locationPrefix} - {DateFormatters.date(new Date(d.ts as number))} ({DateFormatters.time(new Date(d.ts as number))})</span>)
                : <>{DateFormatters.date(start)} ({DateFormatters.time(start)})</>)
            }

        {!start && 'DATUM NI DOLOČEN'}
      </ParticipantsGridCell>
      <ParticipantsGridCell>
        {participant.name && participant.surname ? participant.name + ' ' + participant.surname : participant.nameSurname}
        <div style={{display: 'flex', marginLeft: 'auto', fontSize: '20px', marginRight: '4px'}}>
          {participant.isPartner && <MdHandshake/>}
          {participant.vozilo !== cvv_types.Vozilo.svoje && <MdDriveEta/>}
        </div>


      </ParticipantsGridCell>
      <ParticipantsGridCell style={{justifyContent: 'center'}}>{participant.sklic}</ParticipantsGridCell>
      {/* plačilo */}
      <ParticipantsGridCell style={{justifyContent: 'center', backgroundColor: color, position: 'relative'}}>
        {paymentStatus}{participant.paymentType === billing.PaymentType.kreditna && <CreditCard/>}
      </ParticipantsGridCell>
      {/* ostalo */}
      <ParticipantsGridCell style={{justifyContent: 'center'}}>
        {participant.paymentStatus !== billing.PaymentStatus.paid && <>
          {participant.verified ? 'PREVERJENO' : ''}
        </>}
        {participant.paymentStatus === billing.PaymentStatus.paid && <AttendanceConfirmation participant={participant}/>}
      </ParticipantsGridCell>
    </RowContainer>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <AdminScaffold route={CvvRoutes.admin.prijavljeneOsebe}>
    <Container>
      <AdminHeader>
        <>
          {store.razpisaniTecaj && store.razpisaniTecaj.tecajName}
          {store.razpisaniTecaj && ' (' + DateFormatters.dateTime(new Date(store.razpisaniTecaj.start!.ts! as number)) + ')'}
          {store.razpisaniTecaj && <span
            style={{
              marginLeft: 'auto',
              marginRight: '4px',
              cursor: 'pointer',
              height: '100%',
              display: 'flex',
              justifyContent: "center",
              alignItems: "center"
            }}
            onClick={() => {
              store.razpisaniTecaj = undefined;
              store.participants.loadParticipants();
            }}>
                  <AdminButton onClick={(e) => {
                    e.stopPropagation();
                    store.participants.exportParticipants();
                  }}>IZVOZI</AdminButton>
                <MdClose style={{fontSize: '20px', fontWeight: 'bold', marginLeft: '20px'}}/>
                </span>}</>
        {!store.razpisaniTecaj &&
          <div style={{display: 'flex', width: '100%', justifyContent: 'space-between', flexShrink: 0, alignItems: 'center'}}>
            <div style={{display: 'flex', justifyContent: 'start', whiteSpace: "nowrap", flexWrap: 'wrap', gap: '4px'}}>
              <Select value={store.tecajTip ?? ''}
                      onChange={(e) => {
                        // picking a tip invalidates the chosen tecaj; any filter change
                        // resets pagination and closes the open person (osebe) — like the
                        // old load() did. Sort/pagination deliberately keep the person open.
                        setValues({tip: e.target.value || undefined, tecaj: undefined, page: 0, osebe: undefined});
                      }}>
                <option value={''}>---- izberi tip ----</option>
                {store.meta?.tecajTipi?.map((t) => <option key={t.text!} value={t.text!}>{t.name}</option>)}
              </Select>
              <Select value={store.tecaj ?? ''} onChange={(e) => {
                setValues({tecaj: e.target.value || undefined, page: 0, osebe: undefined});
              }}>
                <option value={''}>---- izberi tecaj ----</option>
                {store.meta &&
                  store.meta?.tecajTipTecaji![store.tecajTip!]?.choices?.map(
                    (t: any) => <option key={t.text!} value={t.text!}>{t.name}</option>
                  )}
              </Select>
              <input type={'date'}
                     value={store.participants.date && DateFormatters.isoDate(store.participants.date)}
                     onChange={(e) => {
                       setValues({date: e.target.valueAsDate || undefined, page: 0, osebe: undefined});
                     }}
              />
              <Select value={store.dodatniPlacilniPogoji ?? ''} onChange={(e) => {
                setValues({placilo: e.target.value || undefined, page: 0, osebe: undefined});
              }}>
                <option value="">---- plačilni pogoji ----</option>
                <option value="ni-predracuna">{CvvStrings.placilo[billing.PaymentStatus.no_proforma_invoice]}</option>
                <option value="predracun-poslan">{CvvStrings.placilo[billing.PaymentStatus.proforma_invoice]}</option>
                <option value="napaka-placila">{CvvStrings.placilo[billing.PaymentStatus.amount_mismatch]}</option>
                <option value="placilo-ok">{CvvStrings.placilo[billing.PaymentStatus.paid]}</option>
                <option value="placilo-na-cvv">PLAČILO NA CVV</option>
                <option value="gratis">BREZPLAČNO</option>
                <option value="racun-izdan">{CvvStrings.placilo[billing.PaymentStatus.invoice_created]}</option>
              </Select>
              <Select value={store.dodatniPogoji ?? ''} onChange={(e) => {
                setValues({pogoji: e.target.value || undefined, page: 0, osebe: undefined});
              }}>
                <option value="">-- dodatni pogoji --</option>
                <option value="brez-datuma">BREZ DATUMA</option>
                <option value="preverjeno">PREVERJENO</option>
                <option value="ne-preverjeno">NEPREVERJENO</option>
                <option value="samo-storno">STORNO</option>
              </Select>
              <input type={'text'}
                     placeholder={'Iskanje'}
                     value={store.participants.search ?? ''}
                     onChange={(e) => {
                       // high-frequency -> replace so we don't spam browser history;
                       // effect (A) debounces the actual load. Clears the open person too.
                       setValues({search: e.target.value || undefined, page: 0, osebe: undefined}, {replace: true});
                     }}/>
            </div>
            {store.tecajTip && store.tecaj && <AdminButton
              onClick={() => {
                navigate(CvvRoutes.novaPrijava(store.tecajTip!, store.tecaj!));
              }}
              style={{marginLeft: 'auto'}}>
              + DODAJ NOVO PRIJAVO
            </AdminButton>}
            <AdminButton onClick={() => store.participants.exportParticipants()}>IZVOZI</AdminButton>
          </div>}

      </AdminHeader>
      <ParticipantsGrid>
        <SortableHeaderCell
          active={store.participants.getSortDirection('tecaj_name') !== null}
          onClick={() => setValues({sort: store.participants.nextSortBy('tecaj_name'), page: 0})}>
          vrsta tečaja {store.participants.getSortDirection('tecaj_name') === 'desc' ? '▼' : store.participants.getSortDirection('tecaj_name') === 'asc' ? '▲' : ''}
        </SortableHeaderCell>
        <SortableHeaderCell
          active={store.participants.getSortDirection('razpisani_tecaj_start') !== null}
          onClick={() => setValues({sort: store.participants.nextSortBy('razpisani_tecaj_start'), page: 0})}>
          datum tečaja {store.participants.getSortDirection('razpisani_tecaj_start') === 'desc' ? '▼' : store.participants.getSortDirection('razpisani_tecaj_start') === 'asc' ? '▲' : ''}
        </SortableHeaderCell>
        <SortableHeaderCell
          active={store.participants.getSortDirection('date_created') !== null}
          onClick={() => setValues({sort: store.participants.nextSortBy('date_created'), page: 0})}>
          oseba {store.participants.getSortDirection('date_created') === 'desc' ? '▼' : store.participants.getSortDirection('date_created') === 'asc' ? '▲' : ''}
        </SortableHeaderCell>
        <SortableHeaderCell
          active={store.participants.getSortDirection('sklic') !== null}
          onClick={() => setValues({sort: store.participants.nextSortBy('sklic'), page: 0})}>
          sklic {store.participants.getSortDirection('sklic') === 'desc' ? '▼' : store.participants.getSortDirection('sklic') === 'asc' ? '▲' : ''}
        </SortableHeaderCell>
        <SortableHeaderCell
          active={store.participants.getSortDirection('payment_status') !== null}
          onClick={() => setValues({sort: store.participants.nextSortBy('payment_status'), page: 0})}>
          plačilo {store.participants.getSortDirection('payment_status') === 'desc' ? '▼' : store.participants.getSortDirection('payment_status') === 'asc' ? '▲' : ''}
        </SortableHeaderCell>
        <ParticipantsGridHeaderCell>ostalo</ParticipantsGridHeaderCell>
        {store.participants.participants?.participants?.map((p) => getRow(p))}
      </ParticipantsGrid>
      <ParticipantsPagination/>
      {store.participants.participant && <AdminParticipant/>}
    </Container>
  </AdminScaffold>
});

const ParticipantsPaginationContainer = styled.div`
    display: flex;
    align-items: center;
    margin: -4px 0 4px auto;
`;
const ParticipantsPagination = observer(() => {
  const store = useContext(AdminStoreContext) as AdminStore;
  const {setValues} = useUrlState(participantsUrlSchema);
  const nPages = store.participants.participants?.nPages ?? 0;
  const cPage = store.participants.page;

  // Write the page to the URL (which flows back into the store and reloads).
  // Returns false when out of bounds, so the number input can restore the value.
  const goPage = (page: number): boolean => {
    if (isNaN(page) || page < 0 || page > nPages || page === cPage) return page === cPage;
    setValues({page});
    return true;
  };

  return <ParticipantsPaginationContainer>
    <IconButton onClick={() => goPage(0)}><MdFirstPage/></IconButton>
    <IconButton onClick={() => goPage(cPage - 1)}><MdChevronLeft/></IconButton>
    <input type={"number"}
           style={{width: '50px', margin: '0 8px 0 8px', borderRadius: '3px', textAlign: 'center'}}
           min={1}
           max={nPages + 1}
           value={cPage + 1}
           onFocus={(e) => e.target.select()}
           onChange={(e) => {
             if (!goPage(e.target.valueAsNumber - 1)) {
               e.preventDefault();
               setTimeout(() => e.target.select(), 0);
             }
           }}/>
    od {(store.participants.participants?.nPages ?? 0) + 1} ({(store).participants.participants?.nItems} {CvvStrings.prijav(store.participants.participants?.nItems ?? 0)})
    <IconButton onClick={() => goPage(cPage + 1)}
                style={{marginLeft: '8px'}}><MdChevronRight/></IconButton>
    <IconButton onClick={() => goPage(nPages)}><MdLastPage/></IconButton>
  </ParticipantsPaginationContainer>;
});


const AttendanceConfirmation = observer(({participant}: { participant: admin_participants.IParticipantSummary }) => {
  const store = useContext(AdminStoreContext) as AdminStore;
  const [state, setState] = useState(false);
  return <div style={{
    display: 'flex',
    alignItems: 'center',
  }}>{
    Array(participant.nDays!).fill(0).map((v, i) => <div
      key={'presence-' + i}
      onClick={(e) => {
        e.stopPropagation();
        store.participants.togglePresence(participant, i).then(() => setState(!state));
      }}
      style={{
        width: '20px',
        height: '25px',
        margin: '2px',
        backgroundColor: participant.daysPresent!.indexOf(i) >= 0 ? 'green' : 'red',
        cursor: 'pointer',
      }}/>)
  }</div>
});
